import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { get, put } from '../utils/apiClient'

const ModuleContext = createContext({
  modules: [],
  loading: true,
  error: null,
  initialized: false,
  isModuleEnabled: () => true,
  updateModuleStatus: async () => {},
  fetchModules: async () => {},
  hasContributionPeriods: false, // ✅ Indique si des périodes de cotisation existent
})

export const useModules = () => {
  const context = useContext(ModuleContext)
  if (!context) {
    throw new Error('useModules doit être utilisé dans un ModuleProvider')
  }
  return context
}

export const ModuleProvider = ({ children }) => {
  const [modules, setModules] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [initialized, setInitialized] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  // ✅ SECURITY: Renommé token -> userId (auth gérée par cookie httpOnly)
  const [currentUserId, setCurrentUserId] = useState(localStorage.getItem('userId'))
  const [hasContributionPeriods, setHasContributionPeriods] = useState(false) // ✅ Périodes de cotisation
  const maxRetries = 3

  const formatErrorMessage = useCallback((error, defaultMessage) => {
    if (error.response?.data?.message) return error.response.data.message
    if (error.message) return error.message
    return defaultMessage
  }, [])

  const fetchModules = useCallback(async () => {
    // ✅ CORRECTION: Ne jamais charger les modules sur routes publiques
    const publicPaths = [
      '/login',
      '/register',
      '/404',
      '/500',
      '/register-organization',
      '/email-validation-pending',
      '/validate-email',
      '/choose-plan',
      '/onboarding',
    ]
    const isPublic = publicPaths.some((path) => window.location.pathname.startsWith(path))

    if (isPublic) {
      console.log('🚫 ModuleContext: Route publique détectée, skip fetchModules')
      setModules([])
      setLoading(false)
      setInitialized(true)
      return
    }

    const token = localStorage.getItem('userId')
    if (!token) {
      setModules([])
      setLoading(false)
      setInitialized(true)
      return
    }

    setLoading(true)
    setError(null)

    try {
      console.log('📦 ModuleContext: Chargement des modules...')
      // ✅ Charger modules ET vérifier si des périodes de cotisation existent
      const [modulesData, periodsResponse] = await Promise.all([
        get('/modules'),
        get('/contributions/periods').catch(() => ({ periods: [] })), // ✅ Silencieux si erreur
      ])
      setModules(modulesData)
      // ✅ Vérifier si au moins une période active existe
      const periods = periodsResponse?.periods || periodsResponse || []
      const activePeriods = Array.isArray(periods) ? periods.filter((p) => p.is_active) : []
      setHasContributionPeriods(activePeriods.length > 0)
      console.log(`✅ ModuleContext: ${modulesData.length} modules, ${activePeriods.length} périodes actives`)
      setError(null)
      setRetryCount(0)
    } catch (error) {
      console.error('❌ ModuleContext: Error fetching modules:', error)
      const errorMessage = formatErrorMessage(error, 'Erreur lors de la récupération des modules')

      if (retryCount < maxRetries && !error.message?.includes('Session expirée')) {
        setRetryCount((prev) => prev + 1)
        setTimeout(() => fetchModules(), 1000 * (retryCount + 1))
      } else {
        setError(errorMessage)
      }
    } finally {
      setLoading(false)
      setInitialized(true)
    }
  }, [retryCount, maxRetries, formatErrorMessage])

  const updateModuleStatus = useCallback(
    async (moduleId, isActive) => {
      const token = localStorage.getItem('userId')
      if (!token) throw new Error('Non authentifié')

      try {
        await put(`/modules/${moduleId}/status`, { isActive })
        await fetchModules()
      } catch (error) {
        console.error('Error updating module status:', error)
        throw error
      }
    },
    [fetchModules],
  )

  const isModuleEnabled = useCallback(
    (moduleName) => {
      if (!initialized || !moduleName) return true
      const module = modules.find((mod) => mod.name === moduleName)
      return module ? module.is_active : false
    },
    [modules, initialized],
  )

  // ✅ CORRECTION: Ne charger les modules que sur les routes protégées
  // Détecter si on est sur une route publique
  const isPublicRoute = () => {
    const publicPaths = [
      '/login',
      '/register',
      '/404',
      '/500',
      '/register-organization',
      '/email-validation-pending',
      '/validate-email',
      '/choose-plan',
      '/onboarding',
    ]
    return publicPaths.some((path) => window.location.pathname.startsWith(path))
  }

  // Effet pour la première initialisation et les changements de token
  useEffect(() => {
    const token = localStorage.getItem('userId')

    // ✅ CORRECTION: Skip si route publique
    if (isPublicRoute()) {
      setModules([])
      setLoading(false)
      setInitialized(true)
      return
    }

    if (token && token !== currentUserId) {
      setCurrentUserId(token)
      fetchModules()
    } else if (!token && currentUserId) {
      setCurrentUserId(null)
      setModules([])
      setLoading(false)
      setInitialized(true)
    } else if (token && !initialized) {
      // Première initialisation uniquement
      fetchModules()
    }
  }, [currentUserId, initialized]) // Dépendances stables uniquement

  // ✅ SECURITY: Effet séparé pour les événements de changement d'authentification
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'userId') {
        const newUserId = localStorage.getItem('userId')
        setCurrentUserId(newUserId)
      }
    }

    const handleAuthChanged = () => {
      const newUserId = localStorage.getItem('userId')
      setCurrentUserId(newUserId)
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('auth_updated', handleAuthChanged)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('auth_updated', handleAuthChanged)
    }
  }, []) // Pas de dépendances - listeners stables

  const value = {
    modules,
    loading,
    error,
    initialized,
    isModuleEnabled,
    updateModuleStatus,
    fetchModules,
    hasContributionPeriods, // ✅ Exposer la présence de périodes de cotisation
  }

  return <ModuleContext.Provider value={value}>{children}</ModuleContext.Provider>
}

export default ModuleProvider
